import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import DutyIcon from '@icons/custom/duty.svg';
import LeadIcon from '@icons/custom/lead-icon.svg';
import PriceListIcon from '@icons/custom/price-list.svg';
import PricingCatalogIcon from '@icons/custom/pricing-catalog.svg';
import ProductParameterIcon from '@icons/custom/product-parameter.svg';
import RuleIcon from '@icons/custom/rules-icon.svg';
import ScoutingIcon from '@icons/custom/scouting-icon.svg';
import SpecificationItemProductIcon from '@icons/custom/specification-item-product.svg';
import SupplierIcon from '@icons/custom/supplier-icon.svg';
import { SvgIconComponent } from '@web/shared/components/svg-icon/svg-icon.component';
import { MenuItem } from 'primeng/api';
import { PanelMenuModule } from 'primeng/panelmenu';

@Component({
  selector: 'app-main-sidebar-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PanelMenuModule, CommonModule, RouterLink, RouterLinkActive, SvgIconComponent],
  template: ` @let _collapsed = collapsed();

    <div class="flex flex-col justify-between gap-12 h-full w-full" [class.collapsed]="_collapsed">
      <p-panelmenu [model]="items()" class="menu-side">
        <ng-template #item let-item>
          <a [routerLink]="item.link" routerLinkActive="p-panelmenu-item-link-active" class="p-panelmenu-header-link">
            @if (item.items && item.items.length) {
              @if (item.expanded) {
                <span class="pi pi-chevron-up"></span>
              } @else {
                <span class="pi pi-chevron-down"></span>
              }
            } @else {
              <div class="flex gap-4">
                @if (item.primeIcon) {
                  <span [class]="item.primeIcon"></span>
                } @else {
                  <app-svg-icon class="size-4" [icon]="item.customIcon" />
                }
              </div>
            }

            @if (!_collapsed) {
              <span class="p-panelmenu-header-label"> {{ item.label }}</span>
            }
          </a>
        </ng-template>
      </p-panelmenu>
      <p-panelmenu [model]="footerItems()" class="menu-side" />
    </div>`,
  styles: `
    :host {
      --p-panelmenu-panel-padding: 0;
      --p-panelmenu-item-padding: 0.75rem 1rem;
      --p-panelmenu-panel-border-width: 0;
      --p-panelmenu-panel-first-border-width: 0;
      --p-panelmenu-panel-last-border-width: 0;
      --p-panelmenu-panel-background: transparent;
      --p-panelmenu-item-color: var(--color-white);
      --p-panelmenu-item-focus-color: var(--color-black);
      --p-panelmenu-item-icon-color: var(--color-white);
      --p-panelmenu-item-icon-focus-color: var(--color-black);
      --p-panelmenu-submenu-icon-color: var(--color-white);
      --p-panelmenu-submenu-icon-focus-color: var(--color-black);
      overflow-y: auto;

      .collapsed {
        --p-panelmenu-item-padding: 0.75rem 1rem;
        --p-panelmenu-submenu-indent: 0.1rem;
      }

      ::ng-deep .menu-side .p-panelmenu-item-link-active {
        background-color: var(--color-primary);
        --p-panelmenu-submenu-icon-color: var(--color-black);
        color: var(--color-black);
        border-radius: 6px;
      }

      ::ng-deep .menu-side p-panelmenulist p-panelmenu-sub ul {
        padding-top: 10px;
        display: grid;
        gap: 10px;
      }

      ::ng-deep .menu-side .p-panelmenu-item-link-active .p-panelmenu-header-label {
        color: var(--color-black);
        font-weight: 700;
      }
    }
  `,
})
export class MainSidebarMenuComponent {
  readonly collapsed = input.required<boolean>();

  readonly items = computed<MenuItem[]>(() => {
    const collapsed = this.collapsed();
    if (collapsed) {
      return this.mainMenuItems.map((item) => {
        return {
          ...item,
          label: undefined,
        };
      });
    }
    return this.mainMenuItems;
  });

  readonly footerItems = computed<MenuItem[]>(() => {
    const collapsed = this.collapsed();
    if (collapsed) {
      return this.footerMenuItems.map((item) => {
        return {
          ...item,
          label: undefined,
        };
      });
    }
    return this.footerMenuItems;
  });

  readonly mainMenuItems: CustomMenuItem[] = [
    {
      label: $localize`Dashboard`,
      link: ['/', 'dashboard'],
      primeIcon: 'pi pi-th-large',
      styleClass: 'leading-none',
    },
    {
      label: $localize`My Company`,
      styleClass: 'leading-none',
      items: [
        {
          label: $localize`General Data`,
          link: ['/', 'owner-company'],
          primeIcon: 'pi pi-briefcase',
          styleClass: 'leading-none',
        },
        {
          label: $localize`Employees`,
          link: ['/', 'employees', 'owner'],
          primeIcon: 'pi pi-id-card',
          styleClass: 'leading-none',
        },
      ],
    },
    {
      label: $localize`Rules`,
      link: ['/', 'rules'],
      customIcon: RuleIcon,
      styleClass: 'leading-none',
    },
    {
      label: $localize`Contacts`,
      link: ['/', 'contacts'],
      primeIcon: 'pi pi-address-book',
      styleClass: 'leading-none',
    },
    {
      label: $localize`Real estates`,
      link: ['/', 'real-estates'],
      primeIcon: 'pi pi-building',
      styleClass: 'leading-none',
    },
    {
      label: $localize`Leads`,
      customIcon: LeadIcon,
      link: ['/', 'leads'],
      styleClass: 'leading-none',
    },
    {
      label: $localize`Scoutings`,
      link: ['/', 'scoutings'],
      customIcon: ScoutingIcon,
      styleClass: 'leading-none',
    },
    {
      label: $localize`Scheduler`,
      link: ['/', 'scheduler'],
      primeIcon: 'pi pi-calendar',
      styleClass: 'leading-none',
    },
    {
      label: $localize`Todo`,
      link: ['/', 'todo'],
      primeIcon: 'pi pi-list-check',
      styleClass: 'leading-none',
    },
    {
      label: $localize`Suppliers`,
      styleClass: 'leading-none',
      items: [
        {
          label: $localize`List`,
          link: ['/', 'suppliers', 'list'],
          customIcon: SupplierIcon,
          styleClass: 'leading-none',
        },
        {
          label: $localize`Employees`,
          link: ['/', 'employees', 'suppliers'],
          primeIcon: 'pi pi-id-card',
          styleClass: 'leading-none',
        },
        {
          label: $localize`Documents`,
          link: ['/', 'suppliers', 'documents'],
          customIcon: SupplierIcon,
          styleClass: 'leading-none',
        },
      ],
    },
    {
      label: $localize`Sales`,
      // routerLink: ['/', 'sales'],
      primeIcon: 'pi pi-money-bill',
      styleClass: 'leading-none',
      items: [
        {
          label: $localize`General Params`,
          link: ['/', 'sales', 'resources'],
          customIcon: ProductParameterIcon,
          styleClass: 'leading-none',
        },
        {
          label: $localize`Duties`,
          link: ['/', 'sales', 'duties'],
          customIcon: DutyIcon,
          styleClass: 'leading-none',
        },
        {
          label: $localize`Products`,
          link: ['/', 'sales', 'products'],
          customIcon: SpecificationItemProductIcon,
          styleClass: 'leading-none',
        },
        {
          label: $localize`Price lists`,
          link: ['/', 'sales', 'price-lists'],
          customIcon: PriceListIcon,
          styleClass: 'leading-none',
        },
        {
          label: $localize`Pricing catalogs`,
          link: ['/', 'sales', 'pricing-catalogs'],
          customIcon: PricingCatalogIcon,
          styleClass: 'leading-none',
        },
      ],
    },
    {
      label: $localize`Biddings`,
      // routerLink: ['/', 'sales'],
      primeIcon: 'pi pi-handshake',
      styleClass: 'leading-none',
      items: [
        {
          label: $localize`Bids`,
          link: ['/', 'biddings', 'bids'],
          customIcon: ProductParameterIcon,
          styleClass: 'leading-none',
        },
      ],
    },
  ];

  readonly footerMenuItems: MenuItem[] = [
    {
      label: $localize`Users`,
      routerLink: ['/', 'membership', 'users'],
      icon: 'pi pi-users',
      styleClass: 'leading-none',
    },
  ];
}

export interface CustomMenuItem {
  label: string;
  link?: string[];
  primeIcon?: string;
  customIcon?: unknown;
  styleClass: string;
  items?: CustomMenuItem[];
}
